import { z } from 'zod';

import type { MatchesSchema } from '@/shared/api/contract';

/**
 * Omnibox — jedno pole, kilka trybów (spec sek. 5 i 6).
 *
 * ⚠ Parser mieszka **na serwerze i niczego nie zapisuje**. Front wysyła surowy tekst,
 * dostaje z powrotem rozpoznaną komendę plus trafienia i na tej podstawie rysuje panel.
 * Powód jest taki sam jak przy normalizacji ogonków: reguły języka komend są częścią
 * produktu, a nie warstwy widoku — dwie implementacje rozjechałyby się przy pierwszej
 * zmianie składni.
 */

/** Prefiks rozpoznany przez serwer: `(brak)` · `?` · `+` · `>` · `!` (spec 6.1). */
export const commandKindSchema = z.enum([
  'search',
  'searchFullText',
  'createElement',
  'createRelation',
  'createCommitment',
]);

export type CommandKind = z.infer<typeof commandKindSchema>;

/**
 * Rozpoznana komenda — **lustro panelu kontekstowego** (spec 5, „panel = lustro komendy").
 *
 * Wszystko jest tu opcjonalne, bo parser ma być **pobłażliwy**: częściowo rozpoznane
 * zdanie też niesie wartość i ma się dać pokazać, zamiast czekać na komplet.
 */
export const parsedCommandSchema = z.object({
  kind: commandKindSchema,
  query: z.string().nullable(),
  title: z.string().nullable(),
  subject: z.string().nullable(),
  targets: z.array(z.string()),
  relation: z.string().nullable(),
  reversed: z.boolean(),
  type: z.string().nullable(),
  description: z.string().nullable(),
  owners: z.array(z.string()),
  in: z.string().nullable(),
  inherit: z.boolean().nullable(),
  iterationNext: z.boolean(),
  iterationNumber: z.number().nullable(),
  surtDoneCommitment: z.string().nullable(),
  /** Klucze i18n — backend nie tłumaczy (spec 4C). */
  problems: z.array(z.string()),
  /** Czy da się to wykonać. `false` = pokaż, czego brakuje, i nie odblokowuj Enter. */
  isComplete: z.boolean(),
});

export type ParsedCommand = z.infer<typeof parsedCommandSchema>;

export const elementSearchHitSchema = z.object({
  elementId: z.number(),
  title: z.string(),
  type: z.string().nullable(),
  /**
   * Tytuł rodzica z `has` (`null` dla korzenia). Przy powtarzalnych tytułach dopiero rodzic
   * czyni wiersz rozróżnialnym — bez niego dwa „Validator: waybills" wyglądają jak dubel.
   */
  parentTitle: z.string().nullable(),
  description: z.string().nullable(),
  score: z.number(),
  /** Trafienie przyszło z opisu, nie z tytułu — warto to pokazać, bo tłumaczy „dlaczego to". */
  matchedInDescription: z.boolean(),
});

export type ElementSearchHit = z.infer<typeof elementSearchHitSchema>;

export const omniboxResultSchema = z.object({
  command: parsedCommandSchema,
  hits: z.array(elementSearchHitSchema),
});

export type OmniboxResult = z.infer<typeof omniboxResultSchema>;

// ── Prefiksy ────────────────────────────────────────────────────────────────

/**
 * Podpowiedź prefiksów pokazywana przy pustym polu.
 *
 * Trzyma się **w modelu, nie w komponencie**, bo to lista zdolności produktu
 * (spec 6.2–6.6), a nie szczegół wyglądu. Ten sam zestaw obsługuje serwerowy parser.
 */
export const PREFIXES = [
  { prefix: '', kind: 'search' },
  { prefix: '?', kind: 'searchFullText' },
  { prefix: '+', kind: 'createElement' },
  { prefix: '>', kind: 'createRelation' },
  { prefix: '!', kind: 'createCommitment' },
] as const satisfies readonly { prefix: string; kind: CommandKind }[];

/** Czy ta komenda coś **tworzy** — a więc czy Enter ma zapisywać, czy nawigować. */
export function isCreation(kind: CommandKind): boolean {
  return kind === 'createElement' || kind === 'createRelation' || kind === 'createCommitment';
}

// ── Spinacz z kontraktem API ────────────────────────────────────────────────
//
// Każdy schemat `zod` zestawiony ze schematem z dokumentu OpenAPI (decyzja 74).
// Bez tych par schematy tego modułu nie byłyby z niczym związane, a rozjazd wróciłby
// do wykrywania w przeglądarce.

export const searchContract = {
  ParsedCommand: true satisfies MatchesSchema<ParsedCommand, 'ParsedCommand'>,
  ElementSearchHit: true satisfies MatchesSchema<ElementSearchHit, 'ElementSearchHit'>,
  OmniboxResult: true satisfies MatchesSchema<OmniboxResult, 'OmniboxResult'>,
} as const;
